// src/views/JuezMunicipal/ModalListaSeguimientos.tsx
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton,
  ModalBody, ModalFooter, Button, Table, Thead, Tr, Th, Td, Tbody,
  Badge, Text, Spinner, Flex
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { listSeguimientos } from '../../services/causasApi';
import type { SeguimientoCausa } from './types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  expediente: string;
}

export default function ModalListaSeguimientos({ isOpen, onClose, expediente }: Props) {
  const [items, setItems] = useState<SeguimientoCausa[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    const run = async () => {
      if (!isOpen || !expediente) return;
      setLoading(true);
      setErr(null);
      try {
        const list = await listSeguimientos(Number(expediente));
        if (alive) setItems(list);
      } catch (e: any) {
        if (alive) setErr(e?.message ?? 'No se pudieron cargar los seguimientos');
      } finally {
        if (alive) setLoading(false);
      }
    };
    run();
    return () => { alive = false; };
  }, [isOpen, expediente]);

  const lista = useMemo(() => {
    const arr = items || [];
    return arr.slice().sort(
      (a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime()
    );
  }, [items]);

  const fmtFecha = (iso: string) => {
    const d = new Date(iso);
    return isNaN(d.getTime())
      ? iso
      : d.toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader
          fontSize="2xl" 
          bgImage="url('/img/bg.webp')"
          bgSize="cover"
          bgPosition="center"
          bgRepeat="no-repeat"
          color="white"
          textShadow="2px 2px 4px rgba(0,0,0,0.7)"
          fontWeight="bold"
          borderTopRadius="md"
          position="relative"
        >
          Seguimientos de la causa: {expediente || '—'}
        </ModalHeader>
        <ModalCloseButton color="white" _hover={{ bg: 'rgba(255,255,255,0.2)' }} />
        <ModalBody maxH="60vh" overflowY="auto">
          {loading ? (
            <Flex align="center" justify="center" py={6}>
              <Spinner mr={3} /> <Text>Cargando seguimientos…</Text>
            </Flex>
          ) : err ? (
            <Text color="red.500">{err}</Text>
          ) : lista.length === 0 ? (
            <Text>No hay seguimientos registrados aún.</Text>
          ) : (
            <Table variant="striped" colorScheme="gray" size="sm">
              <Thead>
                <Tr>
                  <Th>Fecha</Th>
                  <Th>Nota</Th>
                  <Th>Estado nuevo</Th>
                  <Th>Firmado</Th>
                </Tr>
              </Thead>
              <Tbody>
                {lista.map((s) => (
                  <Tr key={s.id}>
                    <Td whiteSpace="nowrap">{fmtFecha(s.fecha)}</Td>
                    <Td maxW="380px" whiteSpace="normal" style={{ wordBreak: 'break-word' }}>
                      {s.nota || '—'}
                    </Td>
                    <Td>{s.estado_nuevo || '—'}</Td>
                    <Td>
                      <Badge colorScheme={s.firmado ? 'green' : 'red'}>
                        {s.firmado ? 'Sí' : 'No'}
                      </Badge>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>Cerrar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
